import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const FrontOfficeViews = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState('current'); // current, past, all
  const [currentGuests, setCurrentGuests] = useState([]);
  const [pastGuests, setPastGuests] = useState([]);
  const [allGuests, setAllGuests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [guestBookingHistory, setGuestBookingHistory] = useState([]);
  const [bookingReceipts, setBookingReceipts] = useState({}); // Store receipts by booking ID
  const [stats, setStats] = useState({
    totalGuests: 0,
    currentStaying: 0,
    totalStays: 0,
    totalRevenue: 0
  });

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6',
    purple: '#8b5cf6'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch current staying guests
      const currentData = await apiGet('/admin/stay-overview/current-guests');
      const current = currentData.success ? currentData.data : [];
      setCurrentGuests(current);

      // Fetch all bookings to get past guests
      const bookingsData = await apiGet('/admin/bookings');
      const allBookings = bookingsData.success ? bookingsData.data : [];

      // Filter past guests (checked out)
      const past = allBookings.filter(b =>
        b.status === 'checked_out' || b.reservation_status === 'checked_out'
      );
      setPastGuests(past);

      // Create unique guest list from all bookings
      const guestMap = new Map();
      allBookings.forEach(booking => {
        // Create a unique key - prioritize email, then phone, then name
        let key;
        if (booking.guest_email && booking.guest_email.trim()) {
          key = `email_${booking.guest_email.toLowerCase().trim()}`;
        } else if (booking.guest_phone && booking.guest_phone.trim()) {
          key = `phone_${booking.guest_phone.trim()}`;
        } else {
          key = `name_${booking.guest_name.toLowerCase().trim()}`;
        }

        if (!guestMap.has(key)) {
          guestMap.set(key, {
            guest_name: booking.guest_name,
            guest_email: booking.guest_email,
            guest_phone: booking.guest_phone,
            guest_nationality: booking.guest_nationality,
            total_bookings: 1,
            total_spent: parseFloat(booking.total_amount || 0),
            last_visit: booking.check_out_date || booking.check_in_date,
            first_visit: booking.check_in_date,
            bookings: [booking.id] // Track booking IDs
          });
        } else {
          const guest = guestMap.get(key);
          guest.total_bookings += 1;
          guest.total_spent += parseFloat(booking.total_amount || 0);
          guest.bookings.push(booking.id);
          if (booking.check_out_date && new Date(booking.check_out_date) > new Date(guest.last_visit || 0)) {
            guest.last_visit = booking.check_out_date;
          }
          if (booking.check_in_date && new Date(booking.check_in_date) < new Date(guest.first_visit || Date.now())) {
            guest.first_visit = booking.check_in_date;
          }
        }
      });

      const allGuestsList = Array.from(guestMap.values());
      setAllGuests(allGuestsList);

      // Calculate stats
      setStats({
        totalGuests: allGuestsList.length,
        currentStaying: current.length,
        totalStays: allBookings.length,
        totalRevenue: allBookings.reduce((sum, b) => sum + parseFloat(b.total_amount || 0), 0)
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const fetchGuestDetails = async (guest) => {
    try {
      const response = await apiGet('/admin/bookings');
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          let guestBookings = [];

          // Match by email first (most accurate)
          if (guest.guest_email && guest.guest_email.trim()) {
            guestBookings = data.data.filter(b =>
              b.guest_email && b.guest_email.toLowerCase().trim() === guest.guest_email.toLowerCase().trim()
            );
          }
          // If no email match, try phone
          else if (guest.guest_phone && guest.guest_phone.trim()) {
            guestBookings = data.data.filter(b =>
              b.guest_phone && b.guest_phone.trim() === guest.guest_phone.trim()
            );
          }
          // Otherwise match by name
          else {
            guestBookings = data.data.filter(b =>
              b.guest_name && b.guest_name.toLowerCase().trim() === guest.guest_name.toLowerCase().trim()
            );
          }

          // Update selected guest with accurate totals
          const totalSpent = guestBookings.reduce((sum, b) => sum + parseFloat(b.total_amount || 0), 0);
          setSelectedGuest({
            ...guest,
            total_bookings: guestBookings.length,
            total_spent: totalSpent
          });
          setGuestBookingHistory(guestBookings);

          // Fetch receipts for all bookings
          fetchBookingReceipts(guestBookings);
        }
      }
    } catch (error) {
      console.error('Error fetching guest details:', error);
    }
  };

  // Function to fetch receipts for all bookings of a guest
  const fetchBookingReceipts = async (bookings) => {
    try {
      const receiptsMap = {};

      // Fetch receipts for each booking
      for (const booking of bookings) {
        const receiptsData = await apiGet('/admin/bookings/${booking.id}/receipts');

        if (receiptsData.success && receiptsData.data && receiptsData.data.length > 0) {
          receiptsMap[booking.id] = receiptsData.data;
        }
      }

      setBookingReceipts(receiptsMap);
    } catch (error) {
      console.error('Error fetching booking receipts:', error);
    }
  };

  // Function to download a specific receipt
  const downloadReceipt = (paymentId, receiptNumber) => {
    const url = `${PRODUCTION_URL}/api/admin/payments/${paymentId}/receipt`;
    window.open(url, '_blank');
  };

  const handleViewDetails = (guest) => {
    setSelectedGuest(guest);
    setShowDetailsModal(true);
    fetchGuestDetails(guest);
  };

  // Filter guests based on search query
  const filterGuests = (guestList) => {
    if (!searchQuery) return guestList;
    return guestList.filter(guest =>
      (guest.guest_name && guest.guest_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (guest.guest_email && guest.guest_email.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (guest.guest_phone && guest.guest_phone.includes(searchQuery)) ||
      (guest.room_number && guest.room_number.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '20px' : '28px',
      padding: isMobile ? '0 8px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '22px' : isTablet ? '26px' : '32px',
      fontWeight: '800',
      color: colors.textDark,
      marginBottom: '8px',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    pageSubtitle: {
      fontSize: isMobile ? '13px' : '15px',
      color: colors.textLight,
      fontWeight: '500'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : isTablet ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
      gap: isMobile ? '12px' : '20px',
      marginBottom: isMobile ? '24px' : '32px'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '18px' : '24px',
      borderRadius: isMobile ? '14px' : '18px',
      boxShadow: '0 4px 16px rgba(45, 80, 22, 0.08)',
      border: `2px solid ${colors.mediumGray}`,
      transition: 'all 0.3s ease'
    },
    statValue: {
      fontSize: isMobile ? '24px' : '32px',
      fontWeight: '800',
      marginBottom: '6px'
    },
    statLabel: {
      fontSize: isMobile ? '10px' : '11px',
      color: colors.textLight,
      textTransform: 'uppercase',
      fontWeight: '700',
      letterSpacing: '0.8px'
    },
    statIcon: {
      fontSize: isMobile ? '20px' : '24px',
      marginBottom: '8px'
    },
    tabContainer: {
      display: 'flex',
      gap: '12px',
      marginBottom: isMobile ? '20px' : '24px',
      borderBottom: `2px solid ${colors.mediumGray}`,
      padding: isMobile ? '0 8px' : '0'
    },
    tab: {
      padding: isMobile ? '10px 16px' : '12px 24px',
      border: 'none',
      background: 'transparent',
      fontSize: isMobile ? '13px' : '15px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      color: colors.textLight,
      borderRadius: '8px 8px 0 0',
      marginBottom: '-2px'
    },
    activeTab: {
      color: colors.primary,
      background: `${colors.primary}08`,
      fontWeight: '700'
    },
    searchBar: {
      width: '100%',
      padding: isMobile ? '12px 16px' : '14px 20px',
      borderRadius: '12px',
      border: `2px solid ${colors.mediumGray}`,
      fontSize: isMobile ? '13px' : '15px',
      marginBottom: isMobile ? '20px' : '24px',
      transition: 'all 0.3s ease'
    },
    guestCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '20px',
      borderRadius: isMobile ? '12px' : '16px',
      marginBottom: isMobile ? '12px' : '16px',
      border: `1px solid ${colors.mediumGray}`,
      boxShadow: '0 2px 12px rgba(45, 80, 22, 0.06)',
      transition: 'all 0.3s ease'
    },
    guestHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '12px'
    },
    guestName: {
      fontSize: isMobile ? '16px' : '18px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '4px'
    },
    guestInfo: {
      fontSize: isMobile ? '12px' : '13px',
      color: colors.textLight,
      marginBottom: '2px'
    },
    guestStats: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)',
      gap: isMobile ? '8px' : '12px',
      paddingTop: '12px',
      borderTop: `1px solid ${colors.mediumGray}`
    },
    statItem: {
      textAlign: 'center'
    },
    statItemValue: {
      fontSize: isMobile ? '16px' : '18px',
      fontWeight: '700',
      color: colors.primary
    },
    statItemLabel: {
      fontSize: isMobile ? '10px' : '11px',
      color: colors.textLight,
      textTransform: 'uppercase',
      fontWeight: '600'
    },
    viewButton: {
      padding: isMobile ? '8px 16px' : '10px 20px',
      background: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: '8px',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      background: colors.white,
      padding: isMobile ? '24px' : '32px',
      borderRadius: isMobile ? '16px' : '20px',
      maxWidth: '700px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
    },
    modalHeader: {
      fontSize: isMobile ? '22px' : '28px',
      fontWeight: '800',
      color: colors.primary,
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: `2px solid ${colors.mediumGray}`
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: '16px',
      marginBottom: '24px'
    },
    infoItem: {
      background: colors.lightGray,
      padding: '16px',
      borderRadius: '12px'
    },
    infoLabel: {
      fontSize: '12px',
      fontWeight: '600',
      color: colors.textLight,
      textTransform: 'uppercase',
      marginBottom: '6px',
      letterSpacing: '0.5px'
    },
    infoValue: {
      fontSize: isMobile ? '15px' : '16px',
      fontWeight: '600',
      color: colors.textDark
    },
    badge: {
      padding: '5px 12px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '700',
      textTransform: 'uppercase',
      letterSpacing: '0.3px'
    },
    closeButton: {
      padding: isMobile ? '10px 20px' : '12px 28px',
      background: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: '10px',
      fontSize: isMobile ? '13px' : '15px',
      fontWeight: '700',
      cursor: 'pointer',
      width: '100%',
      marginTop: '20px',
      transition: 'all 0.3s ease',
      textTransform: 'uppercase'
    },
    emptyState: {
      textAlign: 'center',
      padding: isMobile ? '40px 20px' : '60px 40px',
      color: colors.textLight
    },
    emptyIcon: {
      fontSize: isMobile ? '48px' : '64px',
      marginBottom: '16px'
    },
    emptyText: {
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '600'
    }
  };

  const filteredCurrentGuests = filterGuests(currentGuests);
  const filteredPastGuests = filterGuests(pastGuests);
  const filteredAllGuests = filterGuests(allGuests);

  return (
    <AdminLayout>
      <div style={{ padding: isMobile ? '16px' : '24px', background: colors.lightGray, minHeight: '100vh' }}>
        {/* Page Header */}
        <div style={styles.pageHeader}>
          <h1 style={styles.pageTitle}>
            🏨 Guest Management
          </h1>
          <p style={styles.pageSubtitle}>
            View and manage guest information, booking history, and receipts
          </p>
        </div>

        {/* Stats Cards */}
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statIcon}>👥</div>
            <div style={{ ...styles.statValue, color: colors.primary }}>
              {stats.totalGuests}
            </div>
            <div style={styles.statLabel}>Total Guests</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statIcon}>🛏️</div>
            <div style={{ ...styles.statValue, color: colors.info }}>
              {stats.currentStaying}
            </div>
            <div style={styles.statLabel}>Currently Staying</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statIcon}>📅</div>
            <div style={{ ...styles.statValue, color: colors.accent }}>
              {stats.totalStays}
            </div>
            <div style={styles.statLabel}>Total Stays</div>
          </div>

          <div style={styles.statCard}>
            <div style={styles.statIcon}>💰</div>
            <div style={{ ...styles.statValue, color: colors.success }}>
              ₹{stats.totalRevenue.toLocaleString('en-IN')}
            </div>
            <div style={styles.statLabel}>Total Revenue</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={styles.tabContainer}>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'current' ? styles.activeTab : {})
            }}
            onClick={() => setActiveTab('current')}
            onMouseEnter={(e) => {
              if (activeTab !== 'current') {
                e.currentTarget.style.background = `${colors.lightGray}`;
                e.currentTarget.style.borderBottom = 'none';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'current') {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderBottom = 'none';
              }
            }}
          >
            Current Guests ({currentGuests.length})
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'past' ? styles.activeTab : {})
            }}
            onClick={() => setActiveTab('past')}
            onMouseEnter={(e) => {
              if (activeTab !== 'past') {
                e.currentTarget.style.background = `${colors.lightGray}`;
                e.currentTarget.style.borderBottom = 'none';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'past') {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderBottom = 'none';
              }
            }}
          >
            Past Guests ({pastGuests.length})
          </button>
          <button
            style={{
              ...styles.tab,
              ...(activeTab === 'all' ? styles.activeTab : {})
            }}
            onClick={() => setActiveTab('all')}
            onMouseEnter={(e) => {
              if (activeTab !== 'all') {
                e.currentTarget.style.background = `${colors.lightGray}`;
                e.currentTarget.style.borderBottom = 'none';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'all') {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderBottom = 'none';
              }
            }}
          >
            All Guests ({allGuests.length})
          </button>
        </div>

        {/* Search Bar */}
        <input
          type="text"
          placeholder="Search by name, email, phone, or room..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchBar}
        />

        {/* Guest List */}
        {loading ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>⏳</div>
            <p style={styles.emptyText}>Loading guests...</p>
          </div>
        ) : (
          <>
            {activeTab === 'current' && (
              <>
                {filteredCurrentGuests.length === 0 ? (
                  <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}>🔍</div>
                    <p style={styles.emptyText}>
                      {searchQuery ? 'No guests found matching your search' : 'No current guests'}
                    </p>
                  </div>
                ) : (
                  filteredCurrentGuests.map((guest, index) => (
                    <div key={index} style={styles.guestCard}>
                      <div style={styles.guestHeader}>
                        <div>
                          <div style={styles.guestName}>{guest.guest_name}</div>
                          <div style={styles.guestInfo}>
                            📧 {guest.guest_email || 'N/A'} • 📞 {guest.guest_phone || 'N/A'}
                          </div>
                          <div style={styles.guestInfo}>
                            🏠 Room {guest.room_number || 'N/A'} • 🌍 {guest.guest_nationality || 'N/A'}
                          </div>
                        </div>
                        <button
                          style={styles.viewButton}
                          onClick={() => handleViewDetails(guest)}
                        >
                          View
                        </button>
                      </div>
                      <div style={styles.guestStats}>
                        <div style={styles.statItem}>
                          <div style={styles.statItemValue}>
                            {new Date(guest.check_in_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                          </div>
                          <div style={styles.statItemLabel}>Check-in</div>
                        </div>
                        <div style={styles.statItem}>
                          <div style={styles.statItemValue}>
                            {new Date(guest.check_out_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                          </div>
                          <div style={styles.statItemLabel}>Check-out</div>
                        </div>
                        <div style={styles.statItem}>
                          <div style={styles.statItemValue}>
                            ₹{parseFloat(guest.total_amount || 0).toLocaleString('en-IN')}
                          </div>
                          <div style={styles.statItemLabel}>Total</div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </>
            )}

            {activeTab === 'past' && (
              <>
                {filteredPastGuests.length === 0 ? (
                  <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}>🔍</div>
                    <p style={styles.emptyText}>
                      {searchQuery ? 'No guests found matching your search' : 'No past guests'}
                    </p>
                  </div>
                ) : (
                  filteredPastGuests.map((guest, index) => (
                    <div key={index} style={styles.guestCard}>
                      <div style={styles.guestHeader}>
                        <div>
                          <div style={styles.guestName}>{guest.guest_name}</div>
                          <div style={styles.guestInfo}>
                            📧 {guest.guest_email || 'N/A'} • 📞 {guest.guest_phone || 'N/A'}
                          </div>
                          <div style={styles.guestInfo}>
                            🏠 Room {guest.room_number || 'N/A'} • 🌍 {guest.guest_nationality || 'N/A'}
                          </div>
                        </div>
                        <button
                          style={styles.viewButton}
                          onClick={() => handleViewDetails(guest)}
                        >
                          View
                        </button>
                      </div>
                      <div style={styles.guestStats}>
                        <div style={styles.statItem}>
                          <div style={styles.statItemValue}>
                            {new Date(guest.check_in_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                          </div>
                          <div style={styles.statItemLabel}>Checked In</div>
                        </div>
                        <div style={styles.statItem}>
                          <div style={styles.statItemValue}>
                            {new Date(guest.check_out_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                          </div>
                          <div style={styles.statItemLabel}>Checked Out</div>
                        </div>
                        <div style={styles.statItem}>
                          <div style={styles.statItemValue}>
                            ₹{parseFloat(guest.total_amount || 0).toLocaleString('en-IN')}
                          </div>
                          <div style={styles.statItemLabel}>Total</div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </>
            )}

            {activeTab === 'all' && (
              <>
                {filteredAllGuests.length === 0 ? (
                  <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}>🔍</div>
                    <p style={styles.emptyText}>
                      {searchQuery ? 'No guests found matching your search' : 'No guests'}
                    </p>
                  </div>
                ) : (
                  filteredAllGuests.map((guest, index) => (
                    <div key={index} style={styles.guestCard}>
                      <div style={styles.guestHeader}>
                        <div>
                          <div style={styles.guestName}>{guest.guest_name}</div>
                          <div style={styles.guestInfo}>
                            📧 {guest.guest_email || 'N/A'} • 📞 {guest.guest_phone || 'N/A'}
                          </div>
                          <div style={styles.guestInfo}>
                            🌍 {guest.guest_nationality || 'N/A'}
                          </div>
                        </div>
                        <button
                          style={styles.viewButton}
                          onClick={() => handleViewDetails(guest)}
                        >
                          View
                        </button>
                      </div>
                      <div style={styles.guestStats}>
                        <div style={styles.statItem}>
                          <div style={styles.statItemValue}>{guest.total_bookings}</div>
                          <div style={styles.statItemLabel}>Total Bookings</div>
                        </div>
                        <div style={styles.statItem}>
                          <div style={styles.statItemValue}>
                            ₹{guest.total_spent.toLocaleString('en-IN')}
                          </div>
                          <div style={styles.statItemLabel}>Total Spent</div>
                        </div>
                        <div style={styles.statItem}>
                          <div style={styles.statItemValue}>
                            {guest.last_visit
                              ? new Date(guest.last_visit).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                              : 'N/A'}
                          </div>
                          <div style={styles.statItemLabel}>Last Visit</div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </>
            )}
          </>
        )}

        {/* Guest Details Modal */}
        {showDetailsModal && selectedGuest && (
          <div style={styles.modal} onClick={() => setShowDetailsModal(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <h2 style={styles.modalHeader}>
                Guest Profile
              </h2>

              <h3 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: '700', marginBottom: '20px', color: colors.textDark }}>
                {selectedGuest.guest_name}
              </h3>

              <div style={{ fontSize: '14px', color: colors.textLight, marginBottom: '24px' }}>
                {selectedGuest.guest_phone} • {selectedGuest.guest_email}
              </div>

              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>Nationality</div>
                  <div style={styles.infoValue}>
                    {selectedGuest.guest_nationality || selectedGuest.nationality || 'Indian'}
                  </div>
                </div>

                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>Total Bookings</div>
                  <div style={styles.infoValue}>{selectedGuest.total_bookings || guestBookingHistory.length}</div>
                </div>

                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>Total Spent</div>
                  <div style={styles.infoValue}>
                    ₹{(selectedGuest.total_spent || 0).toLocaleString('en-IN')}
                  </div>
                </div>

                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}>Last Visit</div>
                  <div style={styles.infoValue}>
                    {selectedGuest.last_visit
                      ? new Date(selectedGuest.last_visit).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                      : selectedGuest.check_out_date
                        ? new Date(selectedGuest.check_out_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
                        : 'Currently staying'}
                  </div>
                </div>
              </div>

              <h4 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', color: colors.textDark }}>
                Booking History
              </h4>

              {guestBookingHistory.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '30px',
                  color: colors.textLight,
                  background: colors.lightGray,
                  borderRadius: '12px'
                }}>
                  No booking history available
                </div>
              ) : (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {guestBookingHistory.map((booking, index) => {
                    // Get receipts for this booking
                    const receipts = bookingReceipts[booking.id] || [];

                    return (
                      <div
                        key={index}
                        style={{
                          padding: '16px',
                          background: colors.lightGray,
                          borderRadius: '10px',
                          marginBottom: '12px',
                          border: `1px solid ${colors.mediumGray}`
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '8px'
                        }}>
                          <div>
                            <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '4px' }}>
                              Room {booking.room_number} - {booking.room_type}
                            </div>
                            <div style={{ fontSize: '12px', color: colors.textLight }}>
                              {new Date(booking.check_in_date).toLocaleDateString()} → {new Date(booking.check_out_date).toLocaleDateString()}
                            </div>
                          </div>
                          <span style={{
                            ...styles.badge,
                            background: booking.status === 'checked_out' || booking.reservation_status === 'checked_out'
                              ? 'rgba(107, 168, 62, 0.15)'
                              : booking.status === 'checked_in' || booking.reservation_status === 'checked_in'
                                ? 'rgba(74, 124, 42, 0.15)'
                                : 'rgba(245, 158, 11, 0.15)',
                            color: booking.status === 'checked_out' || booking.reservation_status === 'checked_out'
                              ? colors.accent
                              : booking.status === 'checked_in' || booking.reservation_status === 'checked_in'
                                ? colors.success
                                : colors.warning
                          }}>
                            {booking.reservation_status || booking.status}
                          </span>
                        </div>

                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingTop: '8px',
                          borderTop: `1px solid ${colors.mediumGray}`
                        }}>
                          <div style={{ fontSize: '13px', color: colors.textLight }}>
                            {booking.number_of_guests || 1} guest{(booking.number_of_guests || 1) > 1 ? 's' : ''}
                          </div>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: colors.primary }}>
                            ₹{parseFloat(booking.total_amount || 0).toLocaleString('en-IN')}
                          </div>
                        </div>

                        {/* RECEIPTS SECTION */}
                        {receipts.length > 0 && (
                          <div style={{
                            marginTop: '12px',
                            paddingTop: '12px',
                            borderTop: `1px solid ${colors.mediumGray}`
                          }}>
                            <div style={{
                              fontSize: '12px',
                              fontWeight: '600',
                              color: colors.textLight,
                              marginBottom: '8px'
                            }}>
                              Payment Receipts ({receipts.length})
                            </div>
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '8px'
                            }}>
                              {receipts.map((receipt, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => downloadReceipt(receipt.payment_id, receipt.receipt_number)}
                                  style={{
                                    padding: '6px 12px',
                                    background: colors.success,
                                    color: colors.white,
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = colors.primary;
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = colors.success;
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                  }}
                                >
                                  <span>📄</span>
                                  <span>₹{parseFloat(receipt.amount).toFixed(2)}</span>
                                  <span style={{ fontSize: '9px', opacity: 0.8 }}>
                                    ({new Date(receipt.payment_date).toLocaleDateString('en-IN', {
                                      day: '2-digit',
                                      month: 'short'
                                    })})
                                  </span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {receipts.length === 0 && booking.payment_status === 'completed' && (
                          <div style={{
                            marginTop: '12px',
                            paddingTop: '12px',
                            borderTop: `1px solid ${colors.mediumGray}`,
                            fontSize: '11px',
                            color: colors.textLight,
                            fontStyle: 'italic'
                          }}>
                            No receipts available for this booking
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                style={styles.closeButton}
                onClick={() => setShowDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default FrontOfficeViews;